import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SignOutButton } from '@clerk/clerk-react';
import { useTheme } from '../contexts/ThemeContext.jsx';
import ThemeToggle from './ThemeToggle.jsx';
import AdminNavLink from './AdminNavLink.jsx';

/**
 * Sidebar component with navigation items and collapsible functionality
 */
const Sidebar = ({ 
  items, 
  collapsed, 
  onToggleCollapse, 
  currentPath 
}) => {
  const { theme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isCurrentPath = (href) => {
    if (href === '/dashboard') {
      return currentPath === '/dashboard';
    }
    return currentPath.startsWith(href);
  };

  const sidebarClasses = `
    fixed inset-y-0 left-0 z-50 transition-all duration-300 ease-in-out
    ${collapsed ? 'w-16' : 'w-64'}
    ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border-r border-gray-200'}
    ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
  `;

  return (
    <>
      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75"></div>
        </div>
      )}

      {/* Mobile menu button */}
      <button
        onClick={() => setMobileOpen(true)}
        className={`
          fixed top-4 left-4 z-50 lg:hidden p-2 rounded-md
          ${theme === 'dark' 
            ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }
        `}
      >
        <span className="sr-only">Open sidebar</span>
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Sidebar */}
      <div className={sidebarClasses}>
        {/* Header */}
        <div className={`flex items-center justify-between h-16 px-4 ${
          theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50 border-b border-gray-200'
        }`}>
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src="/logo.svg" alt="ParrotMate Logo" className="h-8 w-8 flex-shrink-0" />
            {!collapsed && (
              <span className={`font-bold text-lg ${
                theme === 'dark' ? 'text-teal-400' : 'text-teal-600'
              }`}>
                ParrotMate
              </span>
            )}
          </Link>
          
          {/* Close button for mobile */}
          <button
            onClick={() => setMobileOpen(false)}
            className={`lg:hidden ${
              theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="sr-only">Close sidebar</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-8 px-4 flex-1">
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={`
                    flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors group
                    ${isCurrentPath(item.href)
                      ? theme === 'dark'
                        ? 'bg-teal-600 text-white'
                        : 'bg-teal-100 text-teal-700'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }
                  `}
                  onClick={() => setMobileOpen(false)}
                  title={collapsed ? item.name : ''}
                >
                  <span className="text-lg flex-shrink-0">
                    {typeof item.icon === 'string' ? item.icon : item.icon}
                  </span>
                  {!collapsed && (
                    <div className="ml-3 flex-1 min-w-0">
                      <span className="block">{item.name}</span>
                      {item.description && (
                        <span className={`block text-xs leading-tight ${
                          isCurrentPath(item.href)
                            ? theme === 'dark' ? 'text-teal-200' : 'text-teal-600'
                            : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {item.description}
                        </span>
                      )}
                    </div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer section */}
        <div className={`p-4 border-t ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          {/* Theme toggle */}
          <div className="mb-4">
            <ThemeToggle collapsed={collapsed} />
          </div>

          {/* Collapse toggle */}
          <button
            onClick={() => onToggleCollapse(!collapsed)}
            className={`
              w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors mb-2
              ${theme === 'dark' 
                ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }
            `}
            title={collapsed ? (collapsed ? 'Expand sidebar' : 'Collapse sidebar') : ''}
          >
            <span className="text-lg flex-shrink-0">
              {collapsed ? '→' : '←'}
            </span>
            {!collapsed && (
              <span className="ml-3">Collapse</span>
            )}
          </button>

          {/* Admin link */}
          <div className="mb-2">
            <AdminNavLink 
              theme={theme} 
              collapsed={collapsed}
              onClick={() => setMobileOpen(false)}
            />
          </div>

          {/* Sign out */}
          <SignOutButton signOutOptions={{ redirectUrl: '/' }}>
            <button
              className={`
                w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors
                ${theme === 'dark' 
                  ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }
              `}
              title={collapsed ? 'Sign Out' : ''}
            >
              <span className="text-lg flex-shrink-0">🚪</span>
              {!collapsed && (
                <span className="ml-3">Sign Out</span>
              )}
            </button>
          </SignOutButton>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
